import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Building2, FolderGit2, Gauge, LayoutGrid, Plus, X } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { OrganizationService, type OrgDetail } from '../services/organizationService';
import { AddRepositoriesModal } from '../components/fleet/AddRepositoriesModal';

function PageSkeleton() {
  return (
    <main className="pt-(--navbar-offset) max-w-5xl mx-auto px-4 pb-20 animate-pulse">
      <div className="mt-8 mb-6 rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="h-8 w-56 rounded bg-gray-200 dark:bg-gray-800 mb-3" />
            <div className="h-4 w-72 rounded bg-gray-100 dark:bg-gray-800/60" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-36 rounded-xl bg-gray-100 dark:bg-gray-800/60" />
            <div className="h-10 w-28 rounded-xl bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:max-w-md">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-24 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40"
            />
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 p-4 space-y-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-9 rounded bg-gray-100 dark:bg-gray-800/60" />
        ))}
      </div>
    </main>
  );
}

export const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await OrganizationService.get(id);
      setOrg(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const removeRepo = async (repoId: string) => {
    if (!id || removingId) return;
    setRemovingId(repoId);
    try {
      await OrganizationService.detachRepositories(id, [repoId]);
      await load();
    } finally {
      setRemovingId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Organization" showDivider={true} />
        <PageSkeleton />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Organization" showDivider={true} />
        <div className="text-center pt-32 text-red-500">{error || 'Not found'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle={org.name} showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-5xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-6 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-100 dark:bg-teal-900/30">
                <Building2 className="w-5.5 h-5.5 text-teal-600 dark:text-teal-400" />
              </span>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {org.name}
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {org.settings?.deliveryMode === 'local_patch'
                    ? 'local patch delivery'
                    : 'PR delivery'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200/70 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm transition-all hover:bg-white dark:hover:bg-gray-800/60 hover:shadow active:scale-[.97]"
              >
                <Plus className="w-4 h-4" /> Add repositories
              </button>
              <Link
                to={`/orgs/${org.id}/fleet`}
                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow active:scale-[.97]"
              >
                <LayoutGrid className="w-4 h-4" /> Fleet view
              </Link>
            </div>
          </div>

          {/* Headline metric tiles */}
          <div className="relative mt-6 grid grid-cols-2 gap-3 sm:max-w-md">
            <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 p-4 transition-all hover:-translate-y-0.5">
              <FolderGit2 className="w-4 h-4 mb-2 text-gray-400 dark:text-gray-500" />
              <div className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                {org.repoCount.toLocaleString()}
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">repositories</div>
            </div>
            <div className="rounded-2xl border border-teal-200/70 dark:border-teal-900 bg-teal-50/70 dark:bg-teal-950/30 p-4 transition-all hover:-translate-y-0.5">
              <Gauge className="w-4 h-4 mb-2 text-teal-500" />
              <div className="text-3xl font-bold tracking-tight tabular-nums text-teal-600 dark:text-teal-400">
                {org.meanScore ?? '—'}
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                mean health score
              </div>
            </div>
          </div>
        </div>

        {showAdd && (
          <AddRepositoriesModal
            orgId={org.id}
            onClose={() => setShowAdd(false)}
            onAttached={load}
          />
        )}

        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-800/50 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <tr>
                <th className="px-4 py-3 font-medium">Repository</th>
                <th className="px-4 py-3 font-medium">Score</th>
                <th className="px-4 py-3 font-medium">Plan items</th>
                <th className="px-4 py-3 font-medium">Automatable</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {org.repositories.map((repo) => (
                <tr
                  key={repo.id}
                  className="border-t border-gray-50 dark:border-gray-800/50 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/repository/${repo.id}`}
                      className="font-medium text-teal-700 dark:text-teal-400 hover:underline"
                    >
                      {repo.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-900 dark:text-gray-200">
                    {repo.latestAnalysisScore ?? '—'}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-400">
                    {repo.maintenancePlan?.totalItems?.toLocaleString() ?? '—'}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-400">
                    {repo.maintenancePlan?.automatableCount?.toLocaleString() ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => removeRepo(repo.id)}
                      disabled={removingId === repo.id}
                      title="Remove from this organization"
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 active:scale-[.97] disabled:opacity-40"
                    >
                      <X className="w-3.5 h-3.5" />
                      {removingId === repo.id ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {org.repositories.length === 0 && (
            <p className="text-center py-10 text-gray-500 dark:text-gray-400">
              No repositories registered yet — run the demo seeder.
            </p>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};
